import React, { createContext, useContext, useState, useEffect } from 'react'
import axios from 'axios'

const AuthContext = createContext(null)

const API_BASE = 'https://dghrik8evf.execute-api.ap-east-1.amazonaws.com'

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Configure axios defaults
  useEffect(() => {
    // Add auth token to all requests
    const token = localStorage.getItem('token')
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
    }

    // Check if user is already logged in
    checkAuth()
  }, [])

  const checkAuth = async () => {
    const token = localStorage.getItem('token')
    if (!token) {
      setLoading(false)
      return
    }

    try {
      const response = await axios.get(`${API_BASE}/auth/me`)
      setUser(response.data.data.user)
    } catch (err) {
      // Only clear token on auth errors (401, 403), not network errors
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('token')
        delete axios.defaults.headers.common['Authorization']
      }
      // For network errors, keep the token and try again later
    } finally {
      setLoading(false)
    }
  }

  const login = async (username, password) => {
    setError(null)
    try {
      const response = await axios.post(`${API_BASE}/auth/login`, {
        username,
        password
      })

      const { token, user } = response.data.data

      // Store token
      localStorage.setItem('token', token)
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`

      setUser(user)
      return { success: true }
    } catch (err) {
      const message = err.response?.data?.message || 'Login failed'
      setError(message)
      return { success: false, error: message }
    }
  }

  const logout = async () => {
    try {
      await axios.post(`${API_BASE}/auth/logout`)
    } catch (err) {
      // Ignore logout errors
    }

    localStorage.removeItem('token')
    delete axios.defaults.headers.common['Authorization']
    setUser(null)
  }

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    checkAuth,
    clearError: () => setError(null)
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export default AuthContext